<script setup lang="ts">
import { onMounted, onUnmounted, ref, useTemplateRef } from 'vue';

import { useLongScroll } from '@libs/utils';

import TheFooter from '../../core/components/theFooter/TheFooter.vue';

interface TripOverviewPageLayoutDesktopEmits {
  (eventName: 'long-scroll'): void;
}
const emit = defineEmits<TripOverviewPageLayoutDesktopEmits>();

const tripOverviewPageLayoutDesktopRef = useTemplateRef(
  'tripOverviewPageLayoutDesktopRef'
);
const contentHeaderRef = useTemplateRef('contentHeaderRef');
const stickySentinelRef = useTemplateRef('stickySentinelRef');
const stickyHeaderRef = useTemplateRef('stickyHeaderRef');

const { longScrollEvent } = useLongScroll(tripOverviewPageLayoutDesktopRef);
longScrollEvent.subscribe(() => emit('long-scroll'));

const isFixed = ref(false);
const stickyHeight = ref(0);
const teleportStyle = ref<Record<string, string>>({
  position: 'fixed',
  top: '0px',
  left: '0px',
  width: '100px',
  zIndex: 'var(--notification-z-index)',
});

let stickyResizeObserver: ResizeObserver | undefined;
let contentResizeObserver: ResizeObserver | undefined;
let sentinelObserver: IntersectionObserver | undefined;

function updateStickyHeight(): void {
  const el = stickyHeaderRef.value as HTMLElement | undefined;
  if (!el) {
    return;
  }

  stickyHeight.value = el.offsetHeight || 0;

  document.documentElement.style.setProperty(
    '--sticky-row-height',
    `${stickyHeight.value}px`
  );
}

function updateTeleportStyle(): void {
  const el = contentHeaderRef.value as HTMLElement | undefined;
  if (!el) {
    return;
  }

  teleportStyle.value = {
    position: 'fixed',
    top: '0px',
    left: '0px',
    width: `calc(100vw - var(--scrollbar-width))`,
    zIndex: 'var(--notification-z-index)',
  };
}

onMounted(() => {
  if (stickyHeaderRef.value) {
    updateStickyHeight();
    stickyResizeObserver = new ResizeObserver(updateStickyHeight);
    stickyResizeObserver.observe(stickyHeaderRef.value);
  }

  if (contentHeaderRef.value) {
    updateTeleportStyle();
    contentResizeObserver = new ResizeObserver(updateTeleportStyle);
    contentResizeObserver.observe(contentHeaderRef.value);
  }

  if (stickySentinelRef.value) {
    sentinelObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const shouldFix = !entry?.isIntersecting;
        if (shouldFix === isFixed.value) {
          return;
        }

        isFixed.value = shouldFix;

        if (shouldFix) {
          updateTeleportStyle();
          window.addEventListener('resize', updateTeleportStyle, {
            passive: true,
          });
          window.addEventListener('scroll', updateTeleportStyle, {
            passive: true,
          });
        } else {
          window.removeEventListener('resize', updateTeleportStyle);
          window.removeEventListener('scroll', updateTeleportStyle);
        }
      },
      { threshold: [0] }
    );
    sentinelObserver.observe(stickySentinelRef.value);
  }

  onUnmounted(() => {
    stickyResizeObserver?.disconnect();
    contentResizeObserver?.disconnect();
    sentinelObserver?.disconnect();
    window.removeEventListener('resize', updateTeleportStyle);
    window.removeEventListener('scroll', updateTeleportStyle);
  });
});
</script>

<template>
  <div
    ref="tripOverviewPageLayoutDesktopRef"
    class="trip-overview-page-layout-desktop"
  >
    <div class="header-wrapper">
      <slot name="shared-header" />

      <div ref="contentHeaderRef" class="content-header">
        <div ref="stickySentinelRef" aria-hidden="true" />

        <div
          v-if="isFixed"
          class="sticky-placeholder"
          :style="{ height: `${stickyHeight}px` }"
          aria-hidden="true"
        />

        <div
          v-if="!isFixed"
          ref="stickyHeaderRef"
          class="sticky-header in-flow"
        >
          <slot name="sticky-header" />
        </div>

        <slot name="header-content" />
      </div>
    </div>

    <div class="content-wrapper">
      <div class="columns-wrapper">
        <div class="left-column">
          <slot name="leftColumn" />
        </div>
        <div class="right-column">
          <slot name="rightColumn" />
        </div>
      </div>
    </div>

    <TheFooter />

    <teleport to="body">
      <div v-if="isFixed" class="sticky-header teleport" :style="teleportStyle">
        <div class="sticky-header-content-wrapper">
          <slot name="sticky-header" />
        </div>
      </div>
    </teleport>
  </div>
</template>

<style scoped lang="scss">
.trip-overview-page-layout-desktop {
  display: flex;
  flex-direction: column;
  background-color: var(--color-off-white);
  min-height: 100vh;
  min-height: 100dvh;
  transform: translateZ(0); // safari fix

  .header-wrapper {
    background-color: var(--color-white);
    border-bottom: 1px solid var(--color-neutral-30);
    box-shadow: var(--shadow-elevation-10);

    .content-header {
      padding-inline: var(--spacing-24);
      max-width: var(--layout-width);
      margin: 0 auto;

      .sticky-placeholder {
        width: 100%;
      }

      .sticky-header.in-flow {
        position: sticky;
        top: 0;
        z-index: var(--notification-z-index);
      }
    }
  }

  .content-wrapper {
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
    grid-template-rows: auto;

    width: 100%;
    max-width: var(--layout-width);
    margin: 0 auto;

    .columns-wrapper {
      --gap: var(--spacing-24);
      display: grid;
      grid-template: inherit;
      grid-column: 1/-1;

      width: 100%;
      padding: var(--spacing-24);
      column-gap: var(--gap);

      .left-column {
        grid-row: 2/-1;
        grid-column: 1/2;
        display: flex;
        flex-direction: column;
        gap: var(--spacing-24);
      }

      .right-column {
        grid-row: 2/-1;
        grid-column: 2/-1;
        position: sticky;
        top: calc(var(--sticky-row-height) + var(--gap));
        height: min-content;
        z-index: var(--modal-overlay-z-index);
      }
    }
  }
}

.sticky-header.teleport {
  box-shadow: var(--shadow-elevation-10);
  background: var(--color-white);
  .sticky-header-content-wrapper {
    padding-inline: var(--spacing-24);
    max-width: var(--layout-width);
    margin: 0 auto;
  }
}
</style>

<style lang="scss">
* {
  box-sizing: border-box;
}
</style>
